import { FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'

import type { ScriptDocument } from '@/api/studio-production'
import { CatalogBadge } from '@/shared/components/catalog/CatalogBadge'
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import { StudioCatalogCard } from '@/features/studio/components/StudioCatalogCard'

export function scriptDisplayName(
  doc: ScriptDocument,
  index: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const title = doc.title?.trim()
  if (title) return title
  return t('script.unnamedScript', { n: index + 1 })
}

function formatScriptLocale(
  locale: string | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const loc = locale?.trim()
  if (!loc) return '—'
  if (loc === 'zh-CN') return t('script.localeZhCN')
  if (loc === 'en-US') return t('script.localeEnUS')
  return loc
}

function formatUpdatedAt(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

type ScriptDocumentCardProps = {
  doc: ScriptDocument
  index: number
  href: string
  overlayActions?: ReactNode
  bottomActions?: ReactNode
  statusBadge?: ReactNode
}

export function ScriptDocumentCard({
  doc,
  index,
  href,
  overlayActions,
  bottomActions,
  statusBadge,
}: ScriptDocumentCardProps) {
  const { t } = useTranslation()
  const name = scriptDisplayName(doc, index, t)
  const cover = resolveMediaDisplayUrl(doc.cover_url)

  return (
    <StudioCatalogCard
      href={href}
      title={name}
      description={doc.summary?.trim() || undefined}
      coverUrl={cover || null}
      fallback={
        <div className="flex h-full flex-col items-center justify-center gap-2 text-amber-500/35">
          <FileText size={36} strokeWidth={1.25} aria-hidden />
          <span className="max-w-[80%] truncate text-2xl font-semibold text-zinc-700">{name.slice(0, 1) || '?'}</span>
        </div>
      }
      badges={
        <>
          {doc.project_type ? (
            <CatalogBadge tone="amber" className="absolute left-3 top-3 z-[2]">
              {t(`production.projectType.${doc.project_type}`, { defaultValue: doc.project_type })}
            </CatalogBadge>
          ) : null}
          {statusBadge ? <div className="absolute right-3 top-3 z-[2]">{statusBadge}</div> : null}
        </>
      }
      overlayActions={overlayActions}
      bottomActions={bottomActions}
      meta={
        <>
          <span>{formatScriptLocale(doc.default_locale, t)}</span>
          <span>{t('script.colEpisodes')}: {doc.episode_count > 0 ? doc.episode_count : doc.episode_target ?? '—'}</span>
          <span>{formatUpdatedAt(doc.updated_at)}</span>
        </>
      }
    />
  )
}
